import 'moment-duration-format';

import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import moment, { unitOfTime } from 'moment';

import {
  API_DATE_FORMAT,
  HOURS_DURATION_FORMAT,
  MINUTES_DURATION_FORMAT,
  MONTH_YEAR_FORMAT,
  TIME_DURATION_FORMAT,
} from 'src/shared/constants';
import { DurationFormatEnums, WeekDaysEnums } from 'src/shared/enums';
import { DateRangeType } from 'src/shared/types';
import { mapEnumKeyToArray } from 'src/shared/utils/arrays';

export const formatDuration = (
  duration: number | undefined,
  durationFormat: DurationFormatEnums,
  unit: 'hours' | 'minutes' | 'seconds' = 'hours',
  as: 'time' | 'days' = 'time',
): string => {
  if (!duration) {
    return '0h';
  }

  const momentDuration = moment.duration(duration, unit);

  if (as === 'days') {
    return momentDuration.asDays().toFixed(1);
  }

  const hours = parseFloat(momentDuration.asHours().toFixed(2));

  let precision = 0;
  let format = HOURS_DURATION_FORMAT;

  if (durationFormat === DurationFormatEnums.T_DECIMAL) {
    if (hours % 1 !== 0) {
      precision = 2;
    }
  } else {
    if (hours % 1 !== 0) {
      format = TIME_DURATION_FORMAT;
    }

    if (Math.abs(hours) < 1) {
      format = MINUTES_DURATION_FORMAT;
    }
  }

  return moment.duration(duration, unit).format(format, precision);
};

export const formatInputDuration = (
  duration: number | string | undefined,
  durationFormat: DurationFormatEnums,
  unit: 'hours' | 'minutes' | 'seconds' = 'seconds',
  pad: boolean = true,
): string => {
  if (!duration) {
    return '';
  }

  const durationIsString = typeof duration === 'string';

  const momentDuration = moment.duration(duration, durationIsString ? undefined : unit);

  if (durationFormat === DurationFormatEnums.T_DECIMAL) {
    return momentDuration.asHours().toFixed(2);
  } else {
    return momentDuration.format(pad ? 'HH:mm' : 'H:mm', { trim: false });
  }
};

export const formatDateTime = (date: string | moment.Moment, dateFormat: string, timeFormat?: string): string => {
  const formattedTime = formatTime(moment(date).format('H:mm'), timeFormat, 'H:mm');
  const formattedDate = moment(date).format(dateFormat);

  return !formattedTime ? formattedDate : `${formattedDate} ${formattedTime}`;
};

export const formatTime = (
  time: string | moment.Moment | null,
  format?: string,
  specificationFormat = 'H:mm',
): string => {
  return format
    ? moment(time, specificationFormat).format(format)
    : moment.duration(moment(time, specificationFormat).format('H:mm')).asHours().toFixed(2);
};

export const formatDurationTimeSpan = (time: string | null, durationFormat = TIME_DURATION_FORMAT): string => {
  return moment(time || '0:00:00', 'H:mm:ss').format(durationFormat);
};

export const formatCalendar = ({ today, yesterday }: { today?: string; yesterday?: string }) => {
  return {
    sameDay: `[${today || 'Today'}]`,
    nextDay: 'ddd D MMM yyyy',
    nextWeek: 'ddd D MMM yyyy',
    lastDay: `[${yesterday || 'Yesterday'}]`,
    lastWeek: 'ddd D MMM yyyy',
    sameElse: 'ddd D MMM yyyy',
  };
};

export const getUtcOffset = (): number => {
  return moment().utcOffset();
};

export const setTimeToDate = (
  date: Date | string | moment.Moment,
  time?: Date | string | moment.Moment | null,
): moment.Moment => {
  if (!time) {
    return moment(date).startOf('day');
  }

  const clone = cloneDeep(moment(date));
  const momentTime = moment(time);

  return clone.set({ hour: momentTime.get('hour'), minute: momentTime.get('minute'), second: 0 });
};

export const isToday = (date: string | moment.Moment): boolean => {
  return moment(date).isSame(new Date(), 'day');
};

export const isSameDay = (date: string | moment.Moment, otherDate: string | moment.Moment): boolean => {
  return moment(date).isSame(otherDate, 'day');
};

export const isBeforeDay = (date: string | moment.Moment, otherDate: string | moment.Moment): boolean => {
  return moment(date).isBefore(otherDate, 'day');
};

export const isAfterDay = (date: string | moment.Moment, otherDate: string | moment.Moment): boolean => {
  return moment(date).isAfter(otherDate, 'day');
};

export const isDateOutOfRange = (date: moment.Moment, targetDate: moment.Moment | null): boolean => {
  if (!targetDate) {
    return date.isBefore(moment().subtract(1, 'days'));
  }

  return date.isBefore(targetDate.startOf('day'));
};

export const isDateWithinDateRange = (
  date: string | moment.Moment,
  range: DateRangeType,
  granularity?: unitOfTime.StartOf,
): boolean => {
  const current = moment(date);
  const { startDate, endDate } = range;

  return (
    current.isBetween(startDate, endDate, granularity) ||
    current.isSame(startDate, granularity) ||
    current.isSame(endDate, granularity)
  );
};

export const isDateWithinCurrentWeek = (date: string | moment.Moment): boolean => {
  const current = moment(date);
  const startDate = moment().startOf('week');
  const endDate = moment().endOf('week');

  return current.isBetween(startDate, endDate) || current.isSame(startDate) || current.isSame(endDate);
};

export const getFirstDayOfMonthByDate = (date: string | moment.Moment): moment.Moment => {
  return moment(date).startOf('month');
};

export const getDateRangeFromMonth = (
  month: string | moment.Moment,
  monthFormat: string = MONTH_YEAR_FORMAT,
  format: string = API_DATE_FORMAT,
): DateRangeType => {
  const monthStart = moment(month, monthFormat).clone().startOf('month');
  const monthEnd = monthStart.clone().endOf('month');
  const startDate = monthStart.clone().startOf('week').format(format);
  const endDate = monthEnd.clone().endOf('week').format(format);

  return { startDate, endDate };
};

export const getWeekDaysEnums = () => {
  return mapEnumKeyToArray(WeekDaysEnums) as (keyof typeof WeekDaysEnums)[];
};

export const validateOverlapDates = <T extends Record<string, any>>(
  list: T[],
  startKey: keyof T,
  endKey: keyof T,
): boolean => {
  // Sort the ranges by start dates in ascending order
  const sortedRanges = sortBy(list, startKey);

  // Check for any overlapping date ranges
  for (let i = 0; i < sortedRanges.length - 1; i++) {
    const currentRange = sortedRanges[i];
    const nextRange = sortedRanges[i + 1];

    if (currentRange[endKey] >= nextRange[startKey]) {
      return false; // Overlapping date ranges found
    }
  }

  return true; // No overlapping date ranges
};
